import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import type { InjectedRouter } from 'react-router';

import { useAddToast } from '../../../contexts/ToastContext';
import { hardRedirectTo } from '../../../utils';
import { presentRequestError } from '../errorPresentation';
import useAuthState from '../hooks/useAuthState';
import { useInviteSignup } from '../hooks/useInviteSignup';
import { useRegister } from '../hooks/useRegister';
import { useRegisterResponseEffect } from '../hooks/useRegisterResponseEffect';
import useResolveUser from '../hooks/useResolveUser';
import AuthLayoutTwoCol from '../layout/AuthLayoutTwoCol';
import ConfirmPasswordStep from '../steps/ConfirmPasswordStep';
import MfaStep from '../steps/MfaStep';
import OrgPickerStep from '../steps/OrgPickerStep';
import { SetPasswordStep } from '../steps/SetPasswordStep';
import { SignupDisabledStep } from '../steps/SignupDisabledStep';
import { SignupEmailStep } from '../steps/SignupEmailStep';
import VerifyEmailStep from '../steps/VerifyEmailStep';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { VerifyEmailSignupVerifiedResponse } from '../../../../shared/types/routes/auth/emailVerification';
import type { RegisterRequest } from '../../../../shared/types/routes/auth/v2';
import type { RouterProps } from '../../../types';

import type { LoginLocationState } from './Login';

export type RegisterPageProps = {
  location: RouterProps['location'];
  /** react-router v3 injects this; only `push` is used, for client-side navigation to the login page. */
  router: Pick<InjectedRouter, 'push'>;
};

// When the entered email already belongs to one or more orgs (or a domain-claim org), the picker offers those plus
// "create a new organization"; a create-new then either confirms an existing password or sets a new one. Confirming
// a password whose account has MFA configured holds at the same challenge step the login flow uses.
type RegisterStep = 'email' | 'org-picker' | 'confirm-password' | 'password' | 'verify' | 'mfa';

const Register = ({ location, router }: RegisterPageProps) => {
  const { inviteToken, next } = location.query;

  const [step, setStep] = useState<RegisterStep>('email');
  const [email, setEmail] = useState(location.query.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [mfaMethod, setMfaMethod] = useState<MfaMethodType | null>(null);

  const {
    orgTitle,
    invitedEmail,
    inviteAlert,
    isEmailLocked,
    isInviteInvalid,
    isInviteResolving,
    registerWithoutInviteHref,
  } = useInviteSignup(inviteToken, next);

  const { data: authState, isError: isAuthStateError, refetch: refetchAuthState } = useAuthState();
  const isIdle = authState?.kind === 'idle';
  const oauthProviders = isIdle ? authState.oauthProviders : [];
  const isSignupDisabled = isIdle ? authState.signupDisabled : false;
  // Where codes can't be mailed (on-prem / flag off) the register endpoint refuses an emailed-code proof, so
  // the confirm step must not offer it. Defaults to false until the auth state lands, so the option only ever
  // appears once it is known to work.
  const canConfirmViaEmail = isIdle ? authState.emailVerificationAvailable : false;

  const resolveUser = useResolveUser();
  const register = useRegister();
  const addToast = useAddToast();

  const heading = orgTitle ? `Sign up and join ${orgTitle}` : 'Sign up';
  const loginQuery = qs.stringify({ email: email || undefined, next }, { skipNulls: true });
  const loginHref = loginQuery ? `/auth/login?${loginQuery}` : '/auth/login';

  // Stable identities: these feed the register settle effect, which must not be re-entered just because this
  // page re-rendered while the mutation result stays latched (mirrors Login.tsx).
  const goToMfa = useCallback((method: MfaMethodType) => {
    setMfaMethod(method);
    setStep('mfa');
  }, []);
  const goToVerify = useCallback(() => setStep('verify'), []);

  const { markAwaited, cancelAwaited } = useRegisterResponseEffect(register.data, {
    next,
    onMfaRequired: goToMfa,
    onVerificationRequired: goToVerify,
  });

  // Once the invite metadata lands, prefill and lock the email to the invited address.
  useEffect(() => {
    if (invitedEmail) {
      setEmail(invitedEmail);
    }
  }, [invitedEmail]);

  // Advance from the settled resolve result rather than the mutate callback: navigating from the callback updates
  // this step while React Query is still notifying subscribers (an unmounted-update warning under React 17).
  useEffect(() => {
    if (!resolveUser.data) {
      return;
    }
    const { candidates, signupSuggestions, hasOrphanAccount, hasPasswordAccount } = resolveUser.data;
    if (isEmailLocked) {
      // Invited: the address is fixed to the inviting org. A password is shared across all of the email's orgs, so
      // an existing password account confirms it rather than setting a second one.
      setStep(hasPasswordAccount ? 'confirm-password' : 'password');
    } else if (hasOrphanAccount) {
      // The email belongs to an account that never finished onboarding. Don't create a duplicate — send them to
      // login (email prefilled) to authenticate org-less and resume onboarding. Source the address from the
      // resolved request, not the live `email` state, so this effect stays keyed to the resolve, not to typing.
      const query = qs.stringify(
        { email: resolveUser.variables?.identifier || undefined, next },
        { skipNulls: true },
      );
      router.push(query ? `/auth/login?${query}` : '/auth/login');
    } else if (candidates.length > 0 || signupSuggestions.length > 0) {
      // The email already has member orgs, or its domain is claimed by one: let the user sign into an existing org
      // or create a new one.
      setStep('org-picker');
    } else {
      setStep('password');
    }
  }, [resolveUser.data, resolveUser.variables, isEmailLocked, next, router]);

  const handleEmailSubmit = () => {
    resolveUser.mutate({ identifier: email, context: 'signup' });
  };

  // Return to the email step to sign up under a different address. The password entries live in the step
  // components, so leaving discards them rather than carrying them over to another account. Memoized because
  // it is handed to `MfaStep` as `onBack`, which feeds it to a settle effect's dependencies (mirrors
  // `Login.tsx`'s `resetToIdentifier`); the setters are stable and `cancelAwaited` is memoized by its hook.
  const handleReset = useCallback(() => {
    cancelAwaited();
    setError(null);
    setStep('email');
  }, [cancelAwaited]);

  // Chosen "create a new organization" from the picker: a returning email with a password confirms it inline; an
  // all-SSO email (or a brand-new email reached via a domain-claim suggestion) sets a new password instead.
  const handleCreateNew = () => {
    setError(null);
    // A confirmable password account (member OR org-less) → confirm it; otherwise set a new password (an all-SSO
    // email verifies by code; a social orphan is already verified). `hasPasswordAccount` spans org-less rows, which
    // the member-only `candidates` don't.
    setStep(resolveUser.data?.hasPasswordAccount ? 'confirm-password' : 'password');
  };

  /** Submit a register request, surfacing a client error inline and anything unexpected as a toast. */
  const submitRegister = (body: RegisterRequest, inlineFallback: string) => {
    if (register.isPending) {
      return;
    }
    setError(null);
    markAwaited();
    register.mutate(body, {
      onError: requestError => {
        const presented = presentRequestError(requestError, {
          inlineFallback,
          toastFallback: 'Something went wrong. Please try again.',
        });
        if (presented.kind === 'inline') {
          setError(presented.message);
          return;
        }
        addToast({ type: 'error', content: presented.message });
      },
    });
  };

  const submitWithPassword = (password: string) =>
    submitRegister(
      { email, password, ...(inviteToken ? { inviteToken } : {}), ...(next ? { next } : {}) },
      'Could not create your account. Please try again.',
    );

  // The email-proof alternative on the confirm-password step: no password is sent — the backend seeds the
  // existing shared hash once the emailed code is confirmed. The settle effect routes the response's
  // emailVerificationRequired onto the verify step like any other pending verification.
  const submitConfirmViaEmail = () =>
    submitRegister(
      { email, confirmVia: 'email', ...(next ? { next } : {}) },
      'Could not send a confirmation code. Please try again.',
    );

  // Where a verified Cloud registration lands, from the confirm response (the account and its auto-created org
  // only exist once the code is confirmed): an org-less account onboards; otherwise honor an explicit `?next=`
  // deep-link (the server echoes the sanitized target as `to`), falling back to first-run project import.
  const resolveVerifiedTarget = (result?: VerifyEmailSignupVerifiedResponse) => {
    if (!result?.organizationId) {
      return '/onboarding';
    }
    return next && result.to ? result.to : '/projects/import';
  };

  const renderStep = () => {
    switch (step) {
      case 'email':
        return isSignupDisabled && !inviteToken ? (
          <SignupDisabledStep loginHref={loginHref} />
        ) : (
          <SignupEmailStep
            heading={heading}
            email={email}
            onEmailChange={setEmail}
            isEmailLocked={isEmailLocked}
            inviteAlert={inviteAlert}
            isInviteInvalid={isInviteInvalid}
            registerWithoutInviteHref={registerWithoutInviteHref}
            // The same conditions that disable Continue must gate the form's onSubmit, which Enter reaches
            // directly — including the not-yet-resolved auth state behind the signup-disabled gate.
            isBlocked={resolveUser.isPending || isInviteInvalid || isInviteResolving || !authState}
            isAuthStateError={isAuthStateError}
            onRetryAuthState={() => refetchAuthState()}
            onSubmit={handleEmailSubmit}
            oauthProviders={oauthProviders}
            next={next}
            inviteToken={inviteToken}
            loginHref={loginHref}
          />
        );
      case 'org-picker':
        return (
          <OrgPickerStep
            candidates={resolveUser.data?.candidates ?? []}
            signupSuggestions={resolveUser.data?.signupSuggestions ?? []}
            next={next}
            // Hand the picked org to the login page so it lands on that org's sign-in method directly (its
            // credential form, or SSO buttons for an SSO-required org) instead of restarting at the identifier
            // step. The query still carries the email so a refresh degrades to the prefilled identifier step.
            onPickCandidate={candidate => router.push({
              pathname: '/auth/login',
              query: { ...(email ? { email } : {}), ...(next ? { next } : {}) },
              state: { methodsOrg: candidate } satisfies LoginLocationState,
            })}
            onCreateNew={handleCreateNew}
            onBack={() => setStep('email')}
          />
        );
      case 'confirm-password':
        return (
          <ConfirmPasswordStep
            email={email}
            isSubmitting={register.isPending}
            error={error}
            // The emailed-code proof creates a new org and carries no invite; the invite already proves the email.
            canConfirmViaEmail={canConfirmViaEmail && !isEmailLocked}
            onSubmit={submitWithPassword}
            onConfirmViaEmail={submitConfirmViaEmail}
            onBack={isEmailLocked ? handleReset : () => {
              cancelAwaited();
              setStep('org-picker');
            }}
            {...(isEmailLocked && {
              heading: orgTitle ? `Join ${orgTitle}` : heading,
              showForgotPassword: true,
              next,
            })}
          />
        );
      case 'password':
        return (
          <SetPasswordStep
            heading={heading}
            email={email}
            error={error}
            isSubmitting={register.isPending}
            onSubmit={submitWithPassword}
            onBack={handleReset}
          />
        );
      case 'verify':
        return (
          <VerifyEmailStep
            email={email}
            onVerified={result => {
              // An email-proof create-new whose account owes a non-email MFA challenge (e.g. TOTP) is held at
              // the challenge by the verify endpoint — same contract as the confirm-password path.
              if (result && 'status' in result) {
                goToMfa(result.mfaMethod);
                return;
              }
              hardRedirectTo(resolveVerifiedTarget(result));
            }}
          />
        );
      case 'mfa':
        return mfaMethod && <MfaStep mfaMethod={mfaMethod} next={next} onBack={handleReset} />;
      default: {
        const exhaustiveCheck: never = step;
        return exhaustiveCheck;
      }
    }
  };

  return <AuthLayoutTwoCol>{renderStep()}</AuthLayoutTwoCol>;
};

export default Register;
